

import { create } from "zustand";
import { UseCaseChat } from "../entities/useCaseChat/UseCaseChat";
import type { UseCaseBrief } from "../entities/useCaseChat/UseCaseBrief";
import { getUseCaseService } from "../services/UseCaseService";
import { DashboardStore } from "./DashboardStore";


interface IAllUseCaseChatsCache {
  useCaseChats: UseCaseChat[]; 
  isLoading: boolean;
  syncFromBriefs: (briefs: UseCaseBrief[], signal?: AbortSignal) => Promise<void>;
}

/**
 * Idea is to have all use cases pre-loaded in a cache, so that when users navigate to the details page, the data is already there and it’s fast.
 */
export class AllUseCaseChatsCache {
  static useAllUseCaseChatsCache = create<IAllUseCaseChatsCache>()((set) => {
     // run id prevents “late” requests from overwriting newer results
    let runId = 0;
    return {
      useCaseChats: [],
      isLoading: false,


      async syncFromBriefs(briefs, signal) {
        const currentRunId = ++runId;
        set({ isLoading: true });
        try {
          const useCaseService = getUseCaseService(); // or another service


           // If polling is frequent, might also want to limit concurrency.
          // For now, keep it parallel.
          const settled = await Promise.allSettled(
            briefs.map(async (b) => {
              // fast abort check
              if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
              return await useCaseService.getUseCaseById(b.useCaseChatId);
            })
          );


          // don’t commit if aborted or superseded
          if (signal?.aborted) return;
          if (currentRunId !== runId) return;

          const results: UseCaseChat[] = settled.map((r, i) => {
            if (r.status === "fulfilled") return r.value;

            const id = briefs[i]?.useCaseChatId;
            console.error(`Failed to fetch details for use case chat id ${id}:`, r.reason);
            return new UseCaseChat({ id });
          });

          set({ useCaseChats: results });
        }catch(err){
          console.error("Error syncing use case chats from briefs:", err);
      
        } finally {

          if (!signal?.aborted && currentRunId === runId) {
            set({ isLoading: false });
          }
        
        }
      },
    }
  });
}






let unsubscribe: (() => void) | null = null;

export function setupBriefsSubscription() {
  if (unsubscribe) return unsubscribe;

  let abort: AbortController | null = null;


  unsubscribe = DashboardStore.useDashboardStore.subscribe(
    (s) => s.useCaseBriefs, 
    async (briefs) => {
  

      abort?.abort();
      abort = new AbortController();

      try {
        await AllUseCaseChatsCache.useAllUseCaseChatsCache
          .getState()
          .syncFromBriefs(briefs, abort.signal);
      } catch (e: unknown ) {
        if (abort.signal.aborted) return;
        console.error("syncFromBriefs failed:", e);
      } 
    },
    { fireImmediately: true }
  );

  return unsubscribe;
}



